import { useEffect, useState } from "react";

const normalizeToss = (tossData) => {
  if (!tossData) return [];

  // New format: object with runners[]
  if (!Array.isArray(tossData) && tossData.runners) {
    return [{
      mid: tossData.marketId,
      mname: tossData.marketName || "Toss",
      sections: (tossData.runners || []).map((runner) => ({
        sid: runner.sid,
        name: runner.name,
        back: runner.odds || 0,
        lay: 0,
      })),
    }];
  }

  // Old format: array of markets with section[]
  if (Array.isArray(tossData)) {
    return tossData.map((market) => ({
      mid: market.mid,
      mname: market.mname || "Toss",
      sections: (market.section || []).map((section) => ({
        sid: section.sid,
        name: section.nat,
        back: section.odds?.find((o) => o.oname === "back1")?.odds || 0,
        lay: section.odds?.find((o) => o.oname === "lay1")?.odds || 0,
      })),
    }));
  }

  return [];
};

const TossTable = ({ oddsData, prevOddsRef, eventSummary }) => {
  const [oddsChanges, setOddsChanges] = useState({});

  const tossRunners = eventSummary?.toss?.runners || [];
  const getExposureBySid = (sid) => tossRunners.find((r) => r.sid === sid)?.exposure ?? null;

  const markets = normalizeToss(oddsData?.toss);

  useEffect(() => {
    if (!markets.length) return;

    const changes = {};
    markets.forEach((market) => {
      market.sections.forEach((section) => {
        ["back", "lay"].forEach((type) => {
          const key = `toss_${section.sid}_${type}`;
          const prevOdd = prevOddsRef.current[key];
          const curr = section[type];
          if (prevOdd !== undefined && curr < prevOdd) {
            changes[key] = true;
            setTimeout(() => {
              setOddsChanges((prev) => {
                const next = { ...prev };
                delete next[key];
                return next;
              });
            }, 2000);
          }
          prevOddsRef.current[key] = curr;
        });
      });
    });

    if (Object.keys(changes).length > 0) setOddsChanges(changes);
  }, [oddsData, markets, prevOddsRef]);

  const isDecreased = (sid, type) => oddsChanges[`toss_${sid}_${type}`];

  if (!markets.length) return null;

  return (
    <>
      {markets.map((market) => {
        if (!market.sections.length) return null;

        return (
          <div key={market.mid} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mt-2">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#0c1524] to-[#162032] px-4 py-2.5 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-1 h-5 bg-teal-400 rounded-full" />
                <span className="text-white font-semibold text-sm tracking-wide">Toss</span>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr>
                    <th className="p-2 border border-slate-200 text-left text-xs font-bold text-slate-500 bg-slate-50" style={{ width: "55%" }}>
                      Market
                    </th>
                    <th className="p-2 border border-slate-200 text-center text-xs font-bold text-white bg-[#64b5f6]" style={{ width: "22.5%" }}>
                      LAGAI
                    </th>
                    <th className="p-2 border border-slate-200 text-center text-xs font-bold text-white bg-[#f48fb1]" style={{ width: "22.5%" }}>
                      KHAI
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {market.sections.map((section) => (
                    <tr key={section.sid} className="border-b border-slate-100">
                      <td className="p-2 border border-slate-200 bg-white">
                        <div className="font-semibold text-slate-800 text-xs">{section.name}</div>
                        {(() => {
                          const exp = getExposureBySid(section.sid);
                          return exp !== null ? (
                            <div className={`text-xs font-medium mt-0.5 ${exp >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                              {exp.toFixed(2)}
                            </div>
                          ) : null;
                        })()}
                      </td>
                      {(() => {
                        const isSuspended = !section.back || section.back === 0;
                        return (
                          <>
                            <td className={`border border-slate-200 text-center p-2 transition-colors duration-300 ${
                              isSuspended ? 'bg-[rgba(22,33,62,0.85)]' : isDecreased(section.sid, "back") ? "bg-red-300" : "bg-[rgb(131,186,232)]"
                            }`}>
                              {isSuspended
                                ? <span className="font-bold text-red-400 text-xs tracking-widest">SUSPENDED</span>
                                : <span className="font-bold text-slate-800 text-sm">{section.back}</span>
                              }
                            </td>
                            <td className={`border border-slate-200 text-center p-2 transition-colors duration-300 ${
                              isSuspended ? 'bg-[rgba(22,33,62,0.85)]' : isDecreased(section.sid, "lay") ? "bg-red-300" : "bg-[rgb(226,142,171)]"
                            }`}>
                              {isSuspended
                                ? <span className="font-bold text-red-400 text-xs tracking-widest">SUSPENDED</span>
                                : <span className="font-bold text-slate-800 text-sm">{section.lay || "-"}</span>
                              }
                            </td>
                          </>
                        );
                      })()}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </>
  );
};

export default TossTable;
